import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Github, Filter, X, Calendar, Tag, Star, TrendingUp, Code, Globe, Smartphone, BookOpen, Briefcase, Zap, Clock, User, Award, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { projectsData } from '../data/projects';

// ── Improvement #13: Smart View Definitions ──────────────────────────────────

type SmartView = 'all' | 'ai-ml' | 'fullstack' | 'product';

interface SmartViewConfig {
  id: SmartView;
  label: string;
  description: string;
  filter: (p: typeof projectsData[0]) => boolean;
}

const AI_ML_CATEGORIES = ['AI/ML', 'Bioinformatics', 'Data Science'];
const AI_ML_TECHS = ['Python', 'PyTorch', 'LLMs', 'Ollama', 'MLflow', 'FastAPI', 'LLM Models', 'AI', 'Neural Networks', 'MLX', 'ESM-2'];
const FULLSTACK_TYPES = ['web', 'mobile', 'desktop'];
const FULLSTACK_COMPLEXITIES = ['High', 'Very High'];
const PRODUCT_TYPES = ['consulting', 'research'];
const PRODUCT_CATEGORIES = ['Product Strategy', 'Energy', 'Finance'];

const smartViews: SmartViewConfig[] = [
  {
    id: 'all',
    label: 'All Projects',
    description: `${projectsData.length}+ projects spanning 20 years of software engineering across AI/ML, FinTech, Security, and more`,
    filter: () => true,
  },
  {
    id: 'ai-ml',
    label: 'AI/ML Focus',
    description: 'Production AI/ML systems — from reinforcement learning training to model evaluation pipelines',
    filter: (p) =>
      AI_ML_CATEGORIES.includes(p.category) ||
      p.technologies.some(t => AI_ML_TECHS.includes(t)),
  },
  {
    id: 'fullstack',
    label: 'Full-Stack Engineering',
    description: 'End-to-end engineering across FinTech, Security, and Platform — Java, TypeScript, Go, Python',
    filter: (p) =>
      FULLSTACK_TYPES.includes(p.type) &&
      FULLSTACK_COMPLEXITIES.includes(p.complexity || ''),
  },
  {
    id: 'product',
    label: 'Product & Strategy',
    description: 'Strategic frameworks and research at the intersection of AI and product management',
    filter: (p) =>
      PRODUCT_TYPES.includes(p.type) ||
      PRODUCT_CATEGORIES.includes(p.category),
  },
];

// ── Component ────────────────────────────────────────────────────────────────

const PortfolioPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [modalProject, setModalProject] = useState<typeof projectsData[0] | null>(null);

  // Improvement #13: Read smart view from URL
  const activeView = (searchParams.get('view') as SmartView) || 'all';
  const activeViewConfig = smartViews.find(v => v.id === activeView) || smartViews[0];

  const setActiveView = (view: SmartView) => {
    if (view === 'all') {
      searchParams.delete('view');
    } else {
      searchParams.set('view', view);
    }
    setSearchParams(searchParams, { replace: true });
    // Clear manual filters when switching views
    clearAllFilters();
  };

  // Sync view from URL on mount
  useEffect(() => {
    const viewParam = searchParams.get('view') as SmartView;
    if (viewParam && !smartViews.find(v => v.id === viewParam)) {
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Get unique values for filters
  const allTechnologies = useMemo(() => {
    const techs = new Set<string>();
    projectsData.forEach(project => {
      project.technologies.forEach(tech => techs.add(tech));
    });
    techs.add('AI');
    techs.add('LLMs');
    return Array.from(techs).sort();
  }, []);

  const allTypes = useMemo(() => {
    const types = new Set<string>();
    projectsData.forEach(project => types.add(project.type));
    return Array.from(types).sort();
  }, []);

  const allYears = useMemo(() => {
    const years = new Set<number>();
    projectsData.forEach(project => years.add(project.year));
    return Array.from(years).sort((a, b) => b - a);
  }, []);

  const allCategories = useMemo(() => {
    const categories = new Set<string>();
    projectsData.forEach(project => categories.add(project.category));
    return Array.from(categories).sort();
  }, []);

  // Filter projects: smart view first, then manual filters
  const filteredProjects = useMemo(() => {
    const filtered = projectsData
      .filter(activeViewConfig.filter)
      .filter(project => {
        const matchesTechnology = selectedTechnologies.length === 0 ||
          selectedTechnologies.some(tech => project.technologies.includes(tech));
        const matchesType = selectedTypes.length === 0 ||
          selectedTypes.includes(project.type);
        const matchesYear = selectedYears.length === 0 ||
          selectedYears.includes(project.year);
        const matchesCategory = selectedCategories.length === 0 ||
          selectedCategories.includes(project.category);
        return matchesTechnology && matchesType && matchesYear && matchesCategory;
      });

    // Featured first, then by year descending
    return filtered.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return b.year - a.year;
    });
  }, [activeViewConfig, selectedTechnologies, selectedTypes, selectedYears, selectedCategories]);

  const toggleTechnology = (tech: string) => {
    setSelectedTechnologies(prev =>
      prev.includes(tech) ? prev.filter(t => t !== tech) : [...prev, tech]
    );
  };

  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleYear = (year: number) => {
    setSelectedYears(prev =>
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const clearAllFilters = () => {
    setSelectedTechnologies([]);
    setSelectedTypes([]);
    setSelectedYears([]);
    setSelectedCategories([]);
  };

  const toggleCardExpand = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasActiveFilters = selectedTechnologies.length > 0 ||
    selectedTypes.length > 0 ||
    selectedYears.length > 0 ||
    selectedCategories.length > 0;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'web': return <Globe size={16} />;
      case 'mobile': return <Smartphone size={16} />;
      case 'research': return <BookOpen size={16} />;
      case 'consulting': return <Briefcase size={16} />;
      default: return <Code size={16} />;
    }
  };

  const getTechnologyColor = (tech: string) => {
    const colors: Record<string, string> = {
      'React': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'TypeScript': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Python': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'Java': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'AI': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'LLMs': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Go': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
      'Kubernetes': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Docker': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Node.js': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'MongoDB': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'MySQL': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'PHP': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'R': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'SAS': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'C++': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Swift': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'PyTorch': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[tech] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'In Development': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Planning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'On Hold': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getRoleBadgeColor = (role?: string) => {
    if (!role) return '';
    if (role.includes('Architect')) return 'bg-indigo-600 text-white dark:bg-indigo-500';
    if (role.includes('Founder')) return 'bg-amber-600 text-white dark:bg-amber-500';
    if (role.includes('Lead')) return 'bg-emerald-600 text-white dark:bg-emerald-500';
    if (role.includes('Strategist')) return 'bg-violet-600 text-white dark:bg-violet-500';
    if (role.includes('Researcher')) return 'bg-cyan-600 text-white dark:bg-cyan-500';
    return 'bg-slate-600 text-white dark:bg-slate-500';
  };

  // Count projects per smart view
  const viewCounts = useMemo(() => {
    const counts: Record<SmartView, number> = { all: 0, 'ai-ml': 0, fullstack: 0, product: 0 };
    for (const view of smartViews) {
      counts[view.id] = projectsData.filter(view.filter).length;
    }
    return counts;
  }, []);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* ── Page Header ── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
            <Code size={24} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-4xl font-bold">Portfolio</h1>
            {/* Improvement #13: Dynamic positioning statement */}
            <p className="text-xl text-muted-foreground">
              {activeViewConfig.description}
            </p>
          </div>
        </div>
      </motion.div>

      {/* ── Improvement #13: Smart View Tabs ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="mb-8"
      >
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {smartViews.map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                ${activeView === view.id
                  ? 'bg-primary-600 text-white shadow-md dark:bg-primary-500'
                  : 'bg-card border text-muted-foreground hover:text-foreground hover:border-primary-300 dark:hover:border-primary-600'
                }
              `}
            >
              {view.label}
              <span className={`ml-2 text-xs ${activeView === view.id ? 'text-primary-200' : 'text-muted-foreground'}`}>
                ({viewCounts[view.id]})
              </span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ── Main Content: Sidebar + Projects ── */}
      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Filter */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8 md:mb-0 md:w-80 shrink-0"
        >
          <div className="bg-card border rounded-lg p-6 shadow-sm sticky top-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-secondary-100 dark:bg-secondary-800 rounded-lg">
                <Filter size={20} className="text-secondary-600 dark:text-secondary-400" />
              </div>
              <h2 className="text-2xl font-semibold">Filter Projects</h2>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {/* Technology Filter */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Code size={18} />
                  Technologies
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                  {allTechnologies.map(tech => (
                    <label key={tech} className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedTechnologies.includes(tech)}
                        onChange={() => toggleTechnology(tech)}
                        className="rounded border-2 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {tech}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Type Filter */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Tag size={18} />
                  Project Type
                </h3>
                <div className="space-y-2">
                  {allTypes.map(type => (
                    <label key={type} className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type)}
                        onChange={() => toggleType(type)}
                        className="rounded border-2 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm capitalize group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {type}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Year Filter */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Calendar size={18} />
                  Year
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                  {allYears.map(year => (
                    <label key={year} className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedYears.includes(year)}
                        onChange={() => toggleYear(year)}
                        className="rounded border-2 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {year}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <TrendingUp size={18} />
                  Category
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                  {allCategories.map(category => (
                    <label key={category} className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => toggleCategory(category)}
                        className="rounded border-2 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {category}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="btn btn-outline text-sm flex items-center gap-2 hover:bg-destructive hover:text-destructive-foreground w-full"
                >
                  <X size={16} />
                  Clear All
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── Projects Section ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex-1"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold">
                Projects ({filteredProjects.length})
              </h2>
              {hasActiveFilters && (
                <span className="bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 text-sm px-3 py-1 rounded-full">
                  Filtered
                </span>
              )}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {filteredProjects.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="text-center py-16 bg-card border rounded-lg"
              >
                <div className="max-w-md mx-auto">
                  <div className="p-4 bg-secondary-100 dark:bg-secondary-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Filter size={24} className="text-secondary-600 dark:text-secondary-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No projects found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters to see more projects.
                  </p>
                  <button
                    onClick={clearAllFilters}
                    className="btn btn-primary"
                  >
                    Clear All Filters
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project, index) => {
                  const isExpanded = expandedCards.has(project.id);
                  return (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className={`group card overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                        project.featured ? 'ring-1 ring-primary-200 dark:ring-primary-800' : ''
                      }`}
                    >
                      {/* Project Image */}
                      <div className="h-48 bg-secondary-100 dark:bg-secondary-800 overflow-hidden">
                        {project.image ? (
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><span class="text-4xl font-bold text-secondary-400">FA</span></div>';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-4xl font-bold text-secondary-400">FA</span>
                          </div>
                        )}
                      </div>

                      {/* Card Content */}
                      <div className="card-content">
                        {/* Title + Featured */}
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="card-title text-lg font-semibold group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors leading-tight">
                            {project.title}
                          </h3>
                          {project.featured && (
                            <span className="shrink-0 ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Star size={10} />
                              Featured
                            </span>
                          )}
                        </div>

                        {/* Improvement #14: Outcome Statement (hero element) */}
                        {project.outcome && (
                          <p className="text-sm font-semibold text-primary-700 dark:text-primary-400 mb-3 leading-snug">
                            {project.outcome}
                          </p>
                        )}

                        {/* Improvement #14: Role Badge + Metadata Row */}
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {project.role && (
                            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ${getRoleBadgeColor(project.role)}`}>
                              <User size={10} />
                              {project.role}
                            </span>
                          )}
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            {getTypeIcon(project.type)}
                            <span className="capitalize">{project.type}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar size={12} />
                            <span>{project.year}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Tag size={12} />
                            <span>{project.category}</span>
                          </div>
                        </div>

                        {/* Description */}
                        <p
                          onClick={() => setModalProject(project)}
                          className="text-sm text-muted-foreground mb-3 line-clamp-3 leading-relaxed cursor-pointer hover:text-foreground transition-colors"
                        >
                          {project.description}
                        </p>

                        {/* Improvement #15: Verification Badges */}
                        {project.verificationBadges && project.verificationBadges.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {project.verificationBadges.map(badge => (
                              <span
                                key={badge}
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                              >
                                <CheckCircle size={10} />
                                {badge}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Technology Tags (collapsed: show 3) */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {project.technologies.slice(0, isExpanded ? undefined : 3).map(tech => (
                            <span
                              key={tech}
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTechnologyColor(tech)}`}
                            >
                              {tech}
                            </span>
                          ))}
                          {!isExpanded && project.technologies.length > 3 && (
                            <button
                              onClick={() => toggleCardExpand(project.id)}
                              className="text-xs text-muted-foreground bg-secondary-100 dark:bg-secondary-800 px-2 py-0.5 rounded-full hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                            >
                              +{project.technologies.length - 3} more
                            </button>
                          )}
                        </div>

                        {/* Improvement #14: Expandable Highlights + Status (expanded view only) */}
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mb-3 space-y-3"
                          >
                            {project.highlights && project.highlights.length > 0 && (
                              <div className="bg-secondary-50 dark:bg-secondary-900/50 rounded-lg p-3 space-y-1.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                                  <Award size={12} />
                                  Key Highlights
                                </span>
                                {project.highlights.map((h, i) => (
                                  <div key={i} className="flex items-start gap-2 text-xs text-foreground">
                                    <Zap size={10} className="mt-0.5 text-primary-500 shrink-0" />
                                    <span>{h}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {project.status && (
                              <div className="flex items-center gap-1 text-xs">
                                <Clock size={12} className="text-muted-foreground" />
                                <span className={`px-2 py-0.5 rounded-full font-medium ${getStatusColor(project.status)}`}>
                                  {project.status}
                                </span>
                              </div>
                            )}
                          </motion.div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between pt-2 border-t border-secondary-100 dark:border-secondary-800">
                          <div className="flex gap-2">
                            {project.link && (
                              <a
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline btn-sm flex items-center gap-1.5 text-xs hover:bg-primary-50 dark:hover:bg-primary-950"
                              >
                                <ExternalLink size={12} />
                                Live Demo
                              </a>
                            )}
                            {project.github && (
                              <a
                                href={project.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline btn-sm flex items-center gap-1.5 text-xs hover:bg-primary-50 dark:hover:bg-primary-950"
                              >
                                <Github size={12} />
                                Code
                              </a>
                            )}
                          </div>
                          {/* Expand/Collapse toggle */}
                          {(project.highlights && project.highlights.length > 0) || project.technologies.length > 3 ? (
                            <button
                              onClick={() => toggleCardExpand(project.id)}
                              className="text-xs text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1"
                            >
                              {isExpanded ? (
                                <>Less <ChevronUp size={12} /></>
                              ) : (
                                <>More <ChevronDown size={12} /></>
                              )}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      {/* Project Detail Modal */}
      <AnimatePresence>
        {modalProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setModalProject(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-card border rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {modalProject.image && (
                <div className="h-56 bg-secondary-100 dark:bg-secondary-800 overflow-hidden rounded-t-xl">
                  <img
                    src={modalProject.image}
                    alt={modalProject.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-2xl font-bold">{modalProject.title}</h2>
                  <button
                    onClick={() => setModalProject(null)}
                    className="p-1 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                {modalProject.outcome && (
                  <p className="text-sm font-semibold text-primary-700 dark:text-primary-400 mb-4">
                    {modalProject.outcome}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {modalProject.role && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ${getRoleBadgeColor(modalProject.role)}`}>
                      <User size={10} />
                      {modalProject.role}
                    </span>
                  )}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {getTypeIcon(modalProject.type)}
                    <span className="capitalize">{modalProject.type}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar size={12} />
                    <span>{modalProject.year}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Tag size={12} />
                    <span>{modalProject.category}</span>
                  </div>
                  {modalProject.status && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(modalProject.status)}`}>
                      {modalProject.status}
                    </span>
                  )}
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {modalProject.description}
                </p>

                {modalProject.longDescription && (
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {modalProject.longDescription}
                  </p>
                )}

                {modalProject.highlights && modalProject.highlights.length > 0 && (
                  <div className="bg-secondary-50 dark:bg-secondary-900/50 rounded-lg p-4 mb-4 space-y-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Award size={12} />
                      Key Highlights
                    </span>
                    {modalProject.highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-foreground">
                        <Zap size={12} className="mt-0.5 text-primary-500 shrink-0" />
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {modalProject.technologies.map(tech => (
                    <span
                      key={tech}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTechnologyColor(tech)}`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {modalProject.verificationBadges && modalProject.verificationBadges.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {modalProject.verificationBadges.map(badge => (
                      <span
                        key={badge}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                      >
                        <CheckCircle size={10} />
                        {badge}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-secondary-100 dark:border-secondary-800">
                  {modalProject.link && (
                    <a
                      href={modalProject.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-sm flex items-center gap-1.5 text-sm"
                    >
                      <ExternalLink size={14} />
                      Live Demo
                    </a>
                  )}
                  {modalProject.github && (
                    <a
                      href={modalProject.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline btn-sm flex items-center gap-1.5 text-sm"
                    >
                      <Github size={14} />
                      Code
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PortfolioPage;
